import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ScanLine, 
  Zap, 
  ShieldCheck, 
  Users, 
  Building2, 
  Target, 
  Menu, 
  X,
  CheckCircle2,
  Clock,
  Eye,
  Heart
} from "lucide-react";

export default function AboutUs() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col min-h-screen font-sans text-gray-900 bg-white selection:bg-blue-200 overflow-x-hidden relative">
      
      {/* 1. HEADER */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
          isScrolled || mobileMenuOpen
            ? "bg-white/90 backdrop-blur-md border-gray-200 py-3 shadow-sm" 
            : "bg-transparent border-transparent py-5"
        }`}
      >
        <div className="flex items-center justify-between w-full max-w-7xl px-4 sm:px-6 mx-auto">
          <h2 
            className="text-xl sm:text-2xl font-black text-gray-900 tracking-tighter cursor-pointer flex items-center gap-1.5" 
            onClick={() => navigate("/")}
          >
            <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-zinc-950 text-white flex items-center justify-center">
              <ScanLine className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </span>
            Expo<span className="text-blue-600">Lead</span>
          </h2>

          <nav className="hidden md:flex items-center gap-8 font-semibold text-gray-600 text-sm">
            <Link to="/" className="hover:text-blue-600 transition">Home</Link>
            <Link to="/features" className="hover:text-blue-600 transition">Features</Link>
            <Link to="/about" className="text-blue-600 font-bold hover:text-blue-700 transition flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
              About Us
            </Link>
          </nav>

          <div className="flex md:hidden items-center gap-2">
            <button 
              onClick={() => navigate("/auth/login")} 
              className="text-xs font-bold text-gray-800 px-2 py-1"
            >
              Log in
            </button>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-800 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-gray-200 px-6 py-4 mt-2 space-y-3 shadow-xl">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block py-2 font-bold text-slate-800 text-sm">Home</Link>
            <Link to="/features" onClick={() => setMobileMenuOpen(false)} className="block py-2 font-bold text-slate-800 text-sm">Features</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 font-bold text-blue-600 text-sm">About Us</Link>
            <button 
              onClick={() => { setMobileMenuOpen(false); navigate("/auth/register"); }} 
              className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl text-sm"
            >
              Get Started
            </button>
          </div>
        )}
      </header>

      {/* 2. HERO SECTION */}
      <main className="flex-grow pt-24 pb-16">
        <section className="relative px-4 sm:px-6 py-12 sm:py-20 text-center max-w-5xl mx-auto">
          
          {/* NS GROUP PRESENTATION BADGE */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 text-xs font-bold text-blue-700 border border-blue-200 bg-blue-50/80 rounded-full shadow-sm">
            <Building2 className="w-4 h-4 text-blue-600" />
            NS Group Presents ExpoLead
          </div>

          <h1 className="text-4xl sm:text-6xl font-black text-slate-900 tracking-tight leading-tight mb-6">
            Eliminating paper card loss. <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600">
              Accelerating B2B trade show deals.
            </span>
          </h1>

          <p className="text-base sm:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto mb-10">
            ExpoLead was engineered by NS Group to solve a multi-billion dollar industry flaw: business cards collected at trade shows getting misplaced, damaged, or followed up days after the exhibition closes. We transform physical booth conversations into real-time digital pipeline conversions in seconds, saving hundreds of team hours and dramatically cutting customer acquisition costs.
          </p>

          {/* STATS STRIP */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 bg-slate-50 p-6 sm:p-8 rounded-3xl border border-slate-200/80 shadow-sm max-w-4xl mx-auto">
            <div>
              <div className="text-3xl sm:text-4xl font-black text-slate-900">&lt; 200ms</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-500 mt-1">AI OCR Speed</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-black text-blue-600">100K+</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-500 mt-1">Cards Digitized</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-black text-emerald-600">90%</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-500 mt-1">Time & Cost Saved</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-black text-slate-900">99.8%</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-500 mt-1">Data Accuracy</div>
            </div>
          </div>
        </section>

        {/* 3. OUR STORY: WHY WE BUILT EXPOLEAD */}
        <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">Our Origin & Purpose</div>
                <h2 className="text-3xl sm:text-4xl font-extrabold mb-6 leading-tight">
                  Built by exhibitors, for exhibitors on the active trade show floor.
                </h2>
                <p className="text-slate-300 leading-relaxed text-sm sm:text-base mb-4">
                  Having organized and participated in dozens of major industrial exhibitions under <strong className="text-white">NS Group (NS Engineers)</strong>, we regularly witnessed a costly operational pattern: sales executives collecting stacks of paper business cards each evening, only to return to the office and spend days manually typing contact data into spreadsheets.
                </p>
                <p className="text-slate-300 leading-relaxed text-sm sm:text-base mb-6">
                  By the time quotes or brochures reached potential buyers, warm exhibition interest had cooled, and faster competitors had already made contact. We engineered ExpoLead to eliminate this lag—giving sales teams a high-speed AI scanner that instantly captures visitor data and triggers automated WhatsApp brochure follow-ups while the prospect is still standing at the booth.
                </p>
                <div className="flex items-center gap-3 text-emerald-400 font-bold text-sm">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                  <span>Trusted across engineering, manufacturing, and enterprise B2B sales organizations.</span>
                </div>
              </div>

              {/* MISSION & VISION CARDS */}
              <div className="space-y-6">
                
                {/* MISSION CARD */}
                <div className="bg-slate-800/80 border border-slate-700/80 p-7 rounded-3xl space-y-3 backdrop-blur-md shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-400 flex items-center justify-center font-black">
                      <Target className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Our Mission</h3>
                  </div>
                  <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                    To empower exhibition teams with zero-friction lead capture and instant digital outreach—ensuring every stall interaction transforms into a trackable, high-converting sales pipeline without manual data entry overhead.
                  </p>
                </div>

                {/* VISION CARD */}
                <div className="bg-slate-800/80 border border-slate-700/80 p-7 rounded-3xl space-y-3 backdrop-blur-md shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 flex items-center justify-center font-black">
                      <Eye className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Our Vision</h3>
                  </div>
                  <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                    To establish the global benchmark for trade show lead intelligence—enabling businesses to maximize event ROI, cut operational expenses, and convert event momentum into lasting commercial growth.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* 4. TIME & COST SAVINGS / CORE PILLARS */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
                Why Industry Leaders Rely on ExpoLead
              </h2>
              <p className="text-slate-600 text-base">
                Purpose-built for speed, engineered for maximum cost-efficiency, and designed to perform under high-density exhibition pressures.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              
              {/* Pillar 1: Time & Cost Savings */}
              <div className="p-8 bg-slate-50 border border-slate-200/80 rounded-3xl space-y-4 hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">
                  <Clock className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Drastic Time & Cost Savings</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Eliminate hundreds of labor hours spent manually typing business card information. Save team salaries and maximize booth rental ROI by capturing leads instantly.
                </p>
              </div>

              {/* Pillar 2: Sub-200ms Execution */}
              <div className="p-8 bg-slate-50 border border-slate-200/80 rounded-3xl space-y-4 hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                  <Zap className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Sub-200ms Neural Recognition</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Our advanced cloud OCR model extracts visitor names, emails, designations, and mobile numbers without manual errors, even in busy or noisy environments.
                </p>
              </div>

              {/* Pillar 3: Team Collaboration & Security */}
              <div className="p-8 bg-slate-50 border border-slate-200/80 rounded-3xl space-y-4 hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center font-bold">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Enterprise Security & Team Sync</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Multiple booth representatives scan cards into one synchronized cloud dashboard. Data is encrypted using AES-256 standards and readily exportable to Excel or CRM systems.
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>

      {/* 5. FOOTER WITH OFFICIAL META BUSINESS PARTNER BADGE & MADE IN INDIA */}
      <footer className="py-10 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
          
          {/* Left Side: Copyright & Legal */}
          <div className="space-y-2 text-center md:text-left">
            <p className="text-sm font-semibold text-gray-700">
              © {new Date().getFullYear()} NS Group. All rights reserved.
            </p>
            <div className="flex justify-center md:justify-start gap-6 text-xs font-medium text-gray-500">
              <Link to="/privacy-policy" className="hover:text-blue-600 transition">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-blue-600 transition">Terms & Conditions</Link>
            </div>
          </div>

          {/* Center Side: Made in India */}
          <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-600">
            <span>Made with</span>
            <Heart className="w-4 h-4 text-rose-500 fill-rose-500 animate-pulse" />
            <span>in India</span>
          </div>

          {/* Right Side: Meta Business Partner Official Image */}
          <div className="flex items-center justify-center">
            <img 
              src="/meta-business-partner.png" 
              alt="Meta Business Partner" 
              className="h-10 sm:h-12 object-contain" 
            />
          </div>

        </div>
      </footer>

    </div>
  );
}